import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { isValidToken, setSession } from '../utils/jwt';
import { authenticationApi } from 'src/api/authentication-api';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  tiles: [],
  rules: [],
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, tiles, rules } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      tiles,
      rules,
    };
  },
  LOGIN: (state, action) => {
    const { user, tiles, rules } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      tiles,
      rules,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    tiles: [],
    rules: [],
  }),
  REGISTER: (state, action) => {
    const { user, tiles, rules } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      tiles,
      rules,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  loginByPassword: () => Promise.resolve(),
  loginByOtp: () => Promise.resolve(),
  sendOtp: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window?.localStorage?.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          authenticationApi
            .userInfo()
            .then((res) => {
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: true,
                  user: {
                    displayName: res.displayName,
                    userName: res.userName,
                    userId: res.userId,
                    validationTypeId: res.validationTypeId,
                  },
                  tiles: res.tiles,
                  rules: res.rules,
                },
              });
            })
            .catch(console.error);
        } else if (accessToken && !isValidToken(accessToken)) {
          refresh();
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              tiles: [],
              rules: [],
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            tiles: [],
            rules: [],
          },
        });
      }
    };

    initialize();
  }, []);

  const refresh = () =>
    authenticationApi
      .refresh()
      .then((response) => {
        var res = response;
        if (res.isSuccess) {
          const { displayName, userName, token, tiles, rules } = JSON.stringify(res);
          setSession(token);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: { displayName, userName },
              tiles,
              rules,
            },
          });
        } else {
          setSession(null);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              tiles: [],
              rules: [],
            },
          });
        }
      })
      .catch((err) => {
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            tiles: [],
            rules: [],
          },
        });
      });

  const loginByPassword = async (username, password) =>
    new Promise((resolve, reject) => {
      authenticationApi
        .login({ username, password })
        .then((result) => {
          if (result.loggedIn) {
            const { displayName, userName, userId, token } = result.loginData;
            setSession(token);
            dispatch({
              type: 'LOGIN',
              payload: {
                user: { displayName, userName, userId },
                tiles: result.tiles,
                rules: result.rules,
              },
            });
            resolve('login-success');
          } else {
            reject(result.message);
          }
        })
        .catch((error) => {
          reject(error.message);
        });
    });

  const loginByOtp = (number, code) =>
    new Promise(async (resolve, reject) => {
      authenticationApi
        .loginByOtp({ number, code })
        .then((result) => {
          if (result.loggedIn) {
            const { displayName, userName, userId, token } = result.loginData;

            setSession(token);
            dispatch({
              type: 'LOGIN',
              payload: {
                user: { displayName, userName, userId },
                tiles: result.tiles,
                rules: result.rules,
              },
            });
            resolve('login-success');
          } else {
            reject(result.message);
          }
        })
        .catch((error) => reject(error.message));
    });

  const register = async (username, password, firstName, lastName) =>
    new Promise(async (resolve, reject) => {
      var dto = { username, password, firstName, lastName };
      authenticationApi
        .register(dto)
        .then((res) => {
          if (res.registered) {
            const { accessToken, user, tiles, rules } = res;
            window?.localStorage?.setItem('accessToken', accessToken);
            dispatch({
              type: 'REGISTER',
              payload: {
                user,
                tiles,
                rules,
              },
            });
            resolve('login-success');
          } else {
            reject(res.message);
          }
        })
        .catch((error) => {
          reject(error.message);
        });
    });

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const sendOtp = authenticationApi.sendOtp;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        loginByPassword,
        loginByOtp,
        logout,
        sendOtp,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
